import { FieldLabel, Select } from '~/components/form';
import { buildActiveProviderModelOptions, isModelIdSelectable } from '~/lib/ai/modelOptions';
import type { ProviderModel } from '~/modules/query/ai/types';

export interface ModelSelectProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  models: ProviderModel[] | undefined;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  testId: string;
}

/**
 * The AI model field. Options are active models only, labelled with their
 * pricing; a saved record whose model has since gone inactive keeps its stored
 * value and shows a warning instead of silently dropping it.
 */
export default function ModelSelect({
  id,
  label,
  value,
  onChange,
  models,
  required,
  disabled,
  error,
  helperText,
  testId,
}: ModelSelectProps) {
  const options = buildActiveProviderModelOptions(models);
  const inactiveSavedValue = Boolean(value) && !isModelIdSelectable(options, value);

  return (
    <div>
      <FieldLabel htmlFor={id} required={required}>
        {label}
      </FieldLabel>
      <Select
        id={id}
        ariaLabel={label}
        testId={testId}
        placeholder="Select a model"
        options={options.map((option) => ({ value: option.id, label: option.label }))}
        value={value}
        disabled={disabled}
        onChange={onChange}
      />
      {error ? (
        <p className="mt-1.5 text-xs text-error-600 dark:text-error-400">{error}</p>
      ) : inactiveSavedValue ? (
        <p className="mt-1.5 text-xs text-warning-600 dark:text-warning-400">
          This model is no longer available. Choose an active model before saving.
        </p>
      ) : helperText ? (
        <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">{helperText}</p>
      ) : null}
    </div>
  );
}
